import { atom } from 'jotai';

export type NavigationBlocker = {
  id: string;
  blocker: () => Promise<boolean> | boolean;
  /**
   * Set when this blocker guards data that a hard reload or tab close would
   * lose, so the guard also arms a `beforeunload` prompt (#10668). Blockers
   * that are registered for the whole life of a screen and only *validate* on
   * the way out must leave this off, or every reload prompts spuriously.
   */
  warnOnUnload?: boolean;
};

/**
 * A navigation that a blocker stopped, parked so the screen that raised the
 * dialog can finish the inspector's original intent (#10668). Resuming through
 * `proceed` lands on the destination they actually asked for — a drawer link
 * goes to that screen, not back one step.
 */
type PendingNavigation = {
  proceed: () => void;
  reset: () => void;
};

// Atom to store registered navigation blockers
const navigationBlockersAtom = atom<NavigationBlocker[]>([]);

const pendingNavigationAtom = atom<PendingNavigation | null>(null);

// Derived atom to check if navigation should be blocked
export const hasNavigationBlockersAtom = atom((get) => {
  const blockers = get(navigationBlockersAtom);
  return blockers.length > 0;
});

/** True when a registered blocker guards data a reload would destroy. */
export const hasUnloadWarningAtom = atom((get) =>
  get(navigationBlockersAtom).some((b) => b.warnOnUnload),
);

// Action atom to register a navigation blocker
export const registerNavigationBlockerAtom = atom(
  null,
  (get, set, blocker: NavigationBlocker) => {
    const currentBlockers = get(navigationBlockersAtom);
    // Remove existing blocker with same id if it exists
    const filteredBlockers = currentBlockers.filter((b) => b.id !== blocker.id);
    set(navigationBlockersAtom, [...filteredBlockers, blocker]);
  },
);

// Action atom to unregister a navigation blocker
export const unregisterNavigationBlockerAtom = atom(
  null,
  (get, set, blockerId: string) => {
    const currentBlockers = get(navigationBlockersAtom);
    const filteredBlockers = currentBlockers.filter((b) => b.id !== blockerId);
    set(navigationBlockersAtom, filteredBlockers);
  },
);

// Action atom to check all blockers and proceed with navigation if allowed
export const checkNavigationBlockersAtom = atom(null, async (get) => {
  const blockers = get(navigationBlockersAtom);
  let canNavigate = true;

  // Every blocker is consulted, never just up to the first objection. A
  // blocker's job includes raising its own dialog, so short-circuiting would
  // leave a second guarded screen's pending state unannounced and a later
  // "leave anyway" would then discard it silently.
  for (const blocker of blockers) {
    const shouldBlock = await blocker.blocker();
    if (shouldBlock) {
      canNavigate = false;
    }
  }

  return canNavigate;
});

/** Called by the router-level guard when a blocker stops a navigation. */
export const parkPendingNavigationAtom = atom(
  null,
  (_get, set, pending: PendingNavigation | null) => {
    set(pendingNavigationAtom, pending);
  },
);

/**
 * Claim the parked navigation without running it, so a screen that has async
 * cleanup to finish first can resume once that work is done. Reading it clears
 * the park, which also means a dismissal reported later in the same click
 * (CustomDialogBox calls `onBackdropClicked` after the button handler) cannot
 * cancel the resume out from under it.
 */
export const takePendingNavigationAtom = atom(null, (get, set) => {
  const pending = get(pendingNavigationAtom);
  set(pendingNavigationAtom, null);
  return pending;
});

/**
 * Resume the navigation a blocker stopped — the "leave anyway" path. Safe to
 * call with nothing parked (the blocker may have already been released), in
 * which case it is a no-op and the inspector stays put.
 */
export const proceedPendingNavigationAtom = atom(null, (get, set) => {
  const pending = get(pendingNavigationAtom);
  set(pendingNavigationAtom, null);
  pending?.proceed();
});

/** Abandon the navigation a blocker stopped — the "stay here" path. */
export const cancelPendingNavigationAtom = atom(null, (get, set) => {
  const pending = get(pendingNavigationAtom);
  set(pendingNavigationAtom, null);
  pending?.reset();
});
